/* eslint-disable unicode-bom */
// Engagement Hub - Messages Page
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import DataTable from '../../components/admin/DataTable';
import LoadingSpinner from '../../components/admin/LoadingSpinner';

const AdminMessages: React.FC = () => {
  const location = useLocation();
  const [messages, setMessages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [selectedMessage, setSelectedMessage] = useState<any>(null);
  const [showModal, setShowModal] = useState(false);

  const fetchMessages = useCallback(async () => {
    try {
      const url = filter === 'all'
        ? 'http://localhost:5000/api/contact'
        : `http://localhost:5000/api/contact?status=${filter}`;
      
      const response = await fetch(url);
      const data = await response.json();
      setMessages(data.messages || []);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching messages:', error);
      setLoading(false);
    }
  }, [filter]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // Open message from notification
  useEffect(() => {
    if (location.state?.openMessage) {
      setSelectedMessage(location.state.openMessage);
      setShowModal(true);
      // Clear the state
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  const updateStatus = async (id: number, status: string) => {
    try {
      await fetch(`http://localhost:5000/api/contact/${id}/status`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status })
      });
      fetchMessages();
    } catch (error) {
      console.error('Error updating status:', error);
    }
  };

  const viewMessage = async (message: any) => {
    setSelectedMessage(message);
    setShowModal(true);
    // Auto mark as read when viewing
    if (message.status === 'unread') {
      await updateStatus(message.id, 'read');
    }
  };

  const columns = [
    { key: 'full_name', label: 'Name', sortable: true },
    { key: 'email', label: 'Email', sortable: true },
    { key: 'subject', label: 'Subject', sortable: true },
    {
      key: 'status',
      label: 'Status',
      render: (value: string) => (
        <span
          style={{
            padding: '4px 10px',
            borderRadius: '6px',
            fontSize: '11px',
            fontWeight: '600',
            textTransform: 'uppercase',
            letterSpacing: '0.03em',
            background:
              value === 'unread'
                ? '#FEE2E2'
                : value === 'read'
                ? '#FED7AA'
                : '#D1FAE5',
            color:
              value === 'unread'
                ? '#991B1B'
                : value === 'read'
                ? '#9A3412'
                : '#065F46',
            border:
              value === 'unread'
                ? '1px solid #FCA5A5'
                : value === 'read'
                ? '1px solid #FDBA74'
                : '1px solid #A7F3D0'
          }}
        >
          {value}
        </span>
      )
    },
    {
      key: 'created_at',
      label: 'Date',
      sortable: true,
      render: (value: string) => new Date(value).toLocaleDateString()
    }
  ];

  if (loading) {
    return (
      <AdminLayout>
        <LoadingSpinner fullScreen />
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div style={{ marginBottom: '28px' }}>
        <h1 style={{ color: '#111827', fontSize: '26px', fontWeight: '700', marginBottom: '6px', letterSpacing: '-0.02em' }}>
          Contact Messages
        </h1>
        <p style={{ color: '#6B7280', fontSize: '14px', fontWeight: '500' }}>
          Manage and respond to contact form submissions
        </p>
      </div>

      {/* Filters */}
      <div style={{ marginBottom: '20px', display: 'flex', gap: '8px' }}>
        {['all', 'unread', 'read', 'replied'].map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            style={{
              padding: '8px 16px',
              borderRadius: '8px',
              border: filter === status ? 'none' : '1px solid #E5E7EB',
              background: filter === status ? '#111827' : '#FFFFFF',
              color: filter === status ? '#FFFFFF' : '#6B7280',
              fontSize: '13px',
              fontWeight: '600',
              cursor: 'pointer',
              transition: 'all 0.15s ease',
              textTransform: 'capitalize'
            }}
            onMouseEnter={(e) => {
              if (filter !== status) {
                e.currentTarget.style.background = '#F9FAFB';
                e.currentTarget.style.color = '#111827';
              }
            }}
            onMouseLeave={(e) => {
              if (filter !== status) {
                e.currentTarget.style.background = '#FFFFFF';
                e.currentTarget.style.color = '#6B7280';
              }
            }}
          >
            {status}
          </button>
        ))}
      </div>

      <DataTable
        columns={columns}
        data={messages}
        actions={(row) => (
          <>
            <button
              onClick={() => viewMessage(row)}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #C7D2FE',
                background: '#EEF2FF',
                color: '#4F46E5',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#4F46E5';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.borderColor = '#4F46E5';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#EEF2FF';
                e.currentTarget.style.color = '#4F46E5';
                e.currentTarget.style.borderColor = '#C7D2FE';
              }}
            >
              View
            </button>
            <button
              onClick={() => updateStatus(row.id, 'read')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #DBEAFE',
                background: '#EFF6FF',
                color: '#2563EB',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#2563EB';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.borderColor = '#2563EB';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#EFF6FF';
                e.currentTarget.style.color = '#2563EB';
                e.currentTarget.style.borderColor = '#DBEAFE';
              }}
            >
              Mark Read
            </button>
            <button
              onClick={() => updateStatus(row.id, 'replied')}
              style={{
                padding: '6px 12px',
                borderRadius: '6px',
                border: '1px solid #D1FAE5',
                background: '#ECFDF5',
                color: '#059669',
                fontSize: '12px',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.15s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#059669';
                e.currentTarget.style.color = 'white';
                e.currentTarget.style.borderColor = '#059669';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = '#ECFDF5';
                e.currentTarget.style.color = '#059669';
                e.currentTarget.style.borderColor = '#D1FAE5';
              }}
            >
              Replied
            </button>
          </>
        )}
      />

      {/* View Message Modal */}
      {showModal && selectedMessage && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '20px'
          }}
          onClick={() => setShowModal(false)}
        >
          <div
            style={{
              background: '#FFFFFF',
              borderRadius: '16px',
              maxWidth: '600px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Modal Header */}
            <div style={{
              padding: '24px',
              borderBottom: '1px solid #E5E7EB',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{ color: '#111827', fontSize: '20px', fontWeight: '700', margin: 0 }}>Contact Message</h2>
              <button
                onClick={() => setShowModal(false)}
                style={{
                  width: '32px',
                  height: '32px',
                  borderRadius: '8px',
                  border: 'none',
                  background: '#F3F4F6',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '18px',
                  color: '#6B7280',
                  transition: 'all 0.15s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#E5E7EB';
                  e.currentTarget.style.color = '#111827';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = '#F3F4F6';
                  e.currentTarget.style.color = '#6B7280';
                }}
              >
                ✕
              </button>
            </div>

            {/* Modal Body */}
            <div style={{ padding: '24px' }}>
              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '12px', fontWeight: '600', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>From</label>
                <p style={{ color: '#111827', fontSize: '15px', fontWeight: '600', margin: 0 }}>{selectedMessage.full_name}</p>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px', marginBottom: '20px' }}>
                <div>
                  <label style={{ display: 'block', color: '#6B7280', fontSize: '12px', fontWeight: '600', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Email</label>
                  <a href={`mailto:${selectedMessage.email}`} style={{ color: '#2563EB', fontSize: '14px', textDecoration: 'none' }}>{selectedMessage.email}</a>
                </div>
                {selectedMessage.phone && (
                  <div>
                    <label style={{ display: 'block', color: '#6B7280', fontSize: '12px', fontWeight: '600', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Phone</label>
                    <a href={`tel:${selectedMessage.phone}`} style={{ color: '#2563EB', fontSize: '14px', textDecoration: 'none' }}>{selectedMessage.phone}</a>
                  </div>
                )}
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '12px', fontWeight: '600', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Subject</label>
                <p style={{ color: '#111827', fontSize: '15px', fontWeight: '600', margin: 0 }}>{selectedMessage.subject}</p>
              </div>

              <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', color: '#6B7280', fontSize: '12px', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Message</label>
                <div style={{
                  background: '#F9FAFB',
                  padding: '16px',
                  borderRadius: '8px',
                  border: '1px solid #E5E7EB'
                }}>
                  <p style={{ color: '#374151', fontSize: '14px', lineHeight: '1.7', margin: 0, whiteSpace: 'pre-wrap' }}>{selectedMessage.message}</p>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '20px' }}>
                <label style={{ color: '#6B7280', fontSize: '12px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Status:</label>
                <span style={{
                  padding: '4px 10px',
                  borderRadius: '6px',
                  fontSize: '11px',
                  fontWeight: '600',
                  textTransform: 'uppercase',
                  background: selectedMessage.status === 'unread' ? '#FEE2E2' : selectedMessage.status === 'read' ? '#FED7AA' : '#D1FAE5',
                  color: selectedMessage.status === 'unread' ? '#991B1B' : selectedMessage.status === 'read' ? '#9A3412' : '#065F46'
                }}>{selectedMessage.status}</span>
                <span style={{ color: '#9CA3AF', fontSize: '13px', marginLeft: 'auto' }}>
                  {new Date(selectedMessage.created_at).toLocaleString()}
                </span>
              </div>

              {/* Action Buttons */}
              <div style={{ display: 'flex', gap: '8px', paddingTop: '20px', borderTop: '1px solid #E5E7EB' }}>
                <button
                  onClick={async () => {
                    await updateStatus(selectedMessage.id, 'replied');
                    setSelectedMessage({ ...selectedMessage, status: 'replied' });
                  }}
                  style={{
                    flex: 1,
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#10B981',
                    color: '#FFFFFF',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#059669'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#10B981'}
                >
                  Mark as Replied
                </button>
                <a
                  href={`mailto:${selectedMessage.email}?subject=Re: ${selectedMessage.subject}`}
                  style={{
                    flex: 1,
                    padding: '10px 20px',
                    borderRadius: '8px',
                    border: 'none',
                    background: '#03A84A',
                    color: '#FFFFFF',
                    fontSize: '13px',
                    fontWeight: '600',
                    cursor: 'pointer',
                    transition: 'all 0.15s ease',
                    textDecoration: 'none',
                    textAlign: 'center',
                    display: 'block'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.background = '#028A3F'}
                  onMouseLeave={(e) => e.currentTarget.style.background = '#03A84A'}
                >
                  Reply via Email
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminMessages;
